import {
  COMPLETED_TASKS_LIMIT,
  PatientBasicData,
  SEARCH_KEYWORD_FLAGGED,
  TaskState,
  TaskType,
  isTaskTypeConcern,
  useGetPatientsQuery,
} from "../features/data/patientApiSlice";
import {
  FilterType,
  FilterTypeInfo,
  QueryStateUpdateFn,
} from "../screens/Patients";
import { List, Typography } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";

import PatientListItem from "./PatientListItem";
import { SelectionSource } from "../features/api/logging";
import { createStyleSheet } from "../utils/styles";
import { useTranslation } from "react-i18next";

export const getPatientListItemId = (patientUid: string) =>
  `PatientListItem_${patientUid}`;

const PatientList = (props: {
  showOnlyActiveTasks: boolean;
  filters: Set<FilterType>;
  searchTerm: string;
  selectedPatientUid: string | null;
  setTypeCounts: (typeCounts: Map<FilterType, FilterTypeInfo>) => void;
  onQueryStateUpdate: QueryStateUpdateFn;
  onTaskSelect: (
    patientUid: string,
    taskUid: string,
    selectionSource: SelectionSource
  ) => void;
  setListNavControls: (params: {
    navPrevious: () => void;
    navNext: () => void;
  }) => void;
  updateListNavState: (params: {
    canNavPrevious: boolean;
    canNavNext: boolean;
  }) => void;
}) => {
  const {
    showOnlyActiveTasks,
    filters,
    searchTerm,
    selectedPatientUid,
    setTypeCounts,
    onQueryStateUpdate,
    onTaskSelect,
    setListNavControls,
    updateListNavState,
  } = props;
  const { t } = useTranslation("Patients");

  // Component state
  const [patientsToDisplay, setPatientsToDisplay] = useState<
    PatientBasicData[] | null
  >(null);

  // Queries/mutations
  const {
    data: patients,
    isFetching,
    isError,
    error,
  } = useGetPatientsQuery(
    {
      state: showOnlyActiveTasks ? TaskState.todo : TaskState.complete,
      filter: searchTerm,
    },
    {
      pollingInterval: 30000,
      skipPollingIfUnfocused: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }
  );
  const { patientBasicData, taskLimitReached } = patients || {};
  const unfilteredPatientBasicData = useGetPatientsQuery(
    {
      state: showOnlyActiveTasks ? TaskState.todo : TaskState.complete,
      filter: "",
    },
    {
      pollingInterval: 30000,
      skipPollingIfUnfocused: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }
  ).data?.patientBasicData;

  // Callbacks
  const handlePatientClick = useCallback(
    (patientUid: string, selectionSource: SelectionSource) => {
      const patientClicked = patientsToDisplay?.find(
        (patient) => patient.uid === patientUid
      );
      const taskUid =
        patientClicked?.tasks.find(
          (t) =>
            t.type !== TaskType.pending_hcw_message &&
            t.type !== TaskType.terms_accepted
        )?.uid || "";
      onTaskSelect(patientUid, taskUid, selectionSource);
    },
    [patientsToDisplay, onTaskSelect]
  );
  const handlePreviousPatient = useCallback(() => {
    const currentIndex = patientsToDisplay?.findIndex(
      (patient) => patient.uid === selectedPatientUid
    );
    if (currentIndex !== undefined && currentIndex > 0) {
      const previousPatient = patientsToDisplay![currentIndex! - 1];
      handlePatientClick(previousPatient.uid, SelectionSource.NavButton);
    }
  }, [patientsToDisplay, selectedPatientUid, handlePatientClick]);
  const handleNextPatient = useCallback(() => {
    const currentIndex = patientsToDisplay?.findIndex(
      (patient) => patient.uid === selectedPatientUid
    );
    if (
      currentIndex !== undefined &&
      currentIndex < patientsToDisplay!.length - 1
    ) {
      const nextPatient = patientsToDisplay![currentIndex! + 1];
      handlePatientClick(nextPatient.uid, SelectionSource.NavButton);
    }
  }, [patientsToDisplay, selectedPatientUid, handlePatientClick]);

  // Reactive code
  useEffect(() => {
    setListNavControls({
      navPrevious: handlePreviousPatient,
      navNext: handleNextPatient,
    });
    return () => {
      setListNavControls({
        navPrevious: () => {},
        navNext: () => {},
      });
    };
  }, [setListNavControls, handlePreviousPatient, handleNextPatient]);

  useEffect(() => {
    const currentIndex =
      patientsToDisplay?.findIndex(
        (patient) => patient.uid === selectedPatientUid
      ) || -1;
    updateListNavState({
      canNavPrevious: currentIndex > 0,
      canNavNext: currentIndex < (patientsToDisplay?.length || 0) - 1,
    });
  }, [selectedPatientUid, updateListNavState, patientsToDisplay]);

  useEffect(() => {
    if (unfilteredPatientBasicData) {
      // Compute filter type counts
      const typeCountMap = new Map<FilterType, FilterTypeInfo>();
      unfilteredPatientBasicData?.forEach((patient) => {
        patient.tasks?.forEach((basicTask) => {
          if (
            basicTask.type === TaskType.request_to_chat ||
            basicTask.type === TaskType.pending_hcw_message ||
            basicTask.type === TaskType.urgent_request_to_chat
          ) {
            typeCountMap.set(FilterType.ReadyToChat, {
              count: (typeCountMap.get(FilterType.ReadyToChat)?.count || 0) + 1,
              flaggedForSupervisor:
                typeCountMap.get(FilterType.ReadyToChat)
                  ?.flaggedForSupervisor || basicTask.flagged_for_supervisor,
            });
          } else if (basicTask.type === TaskType.followup) {
            typeCountMap.set(FilterType.FollowUps, {
              count: (typeCountMap.get(FilterType.FollowUps)?.count || 0) + 1,
              flaggedForSupervisor:
                typeCountMap.get(FilterType.FollowUps)?.flaggedForSupervisor ||
                basicTask.flagged_for_supervisor,
            });
          } else {
            typeCountMap.set(FilterType.Concerns, {
              count: (typeCountMap.get(FilterType.Concerns)?.count || 0) + 1,
              flaggedForSupervisor:
                typeCountMap.get(FilterType.Concerns)?.flaggedForSupervisor ||
                basicTask.flagged_for_supervisor,
            });
          }
        });
      });
      setTypeCounts(typeCountMap);
    }
  }, [unfilteredPatientBasicData, setTypeCounts]);

  useEffect(() => {
    if (patientBasicData) {
      // Compute task list to display
      const filteredPatients =
        patientBasicData?.filter((patient, i) =>
          patient.tasks?.some(
            (basicTask) =>
            (!searchTerm ||
              searchTerm !== SEARCH_KEYWORD_FLAGGED ||
              basicTask.flagged_for_supervisor) &&
              ((filters.has(FilterType.ReadyToChat) &&
              (basicTask.type === TaskType.request_to_chat ||
                basicTask.type === TaskType.pending_hcw_message ||
                basicTask.type === TaskType.urgent_request_to_chat)) ||
                (filters.has(FilterType.FollowUps) &&
                  basicTask.type === TaskType.followup) ||
                (filters.has(FilterType.Concerns) &&
                  isTaskTypeConcern(basicTask.type)))
          )
        ) || [];
      setPatientsToDisplay(filteredPatients);
    }
  }, [patientBasicData, searchTerm, filters]);

  useEffect(() => {
    onQueryStateUpdate(
      (patientsToDisplay?.length || 0) === 0,
      isFetching,
      isError,
      error
    );
  }, [onQueryStateUpdate, isFetching, isError, error, patientsToDisplay]);

  return (
    <List sx={styles.listContainer}>
      {patientsToDisplay?.map((patient, i) => (
        <PatientListItem
          id={getPatientListItemId(patient.uid)}
          key={patient.uid}
          patient={patient}
          onClick={handlePatientClick}
          selected={patient.uid === selectedPatientUid}
        />
      ))}
      {taskLimitReached && (
        <Typography variant="body2" sx={styles.taskLimitReachedText}>
          {t("taskLimitReached", { count: COMPLETED_TASKS_LIMIT })}
        </Typography>
      )}
    </List>
  );
};

const styles = createStyleSheet({
  listContainer: {
    overflowY: "scroll",
    paddingY: 0,
  },
  taskLimitReachedText: {
    padding: 2,
    color: "GrayText",
  },
});

export default PatientList;
